import { getRoomColor } from "./FloorplanRoomOverlay";
import { OccupancyStripes, freeColor, hasOwnColor, occupiedFill, occupancyColor, stripedPatternId } from "./occupancyFill";
import type { FurnitureItem } from "./furniture/types";
import type { FloorplanRoom } from "./types";

/**
 * The "Occupancy" layer: every room a presence sensor reports on is tinted (`./occupancyFill`) —
 * free rooms solid green (or their own status colour, never striped), busy rooms diagonal red stripes
 * alternating with the room's own status colour when it has one (a meeting room starting soon, a
 * leased room ...). A room without a sensor, or whose state is unknown, draws nothing.
 * Staff always get `inUse` from the backend; members only for rooms whose settings share it.
 */
const STYLE = `
.nodus-fp-occ-room polygon {
  stroke-width: 2px;
  vector-effect: non-scaling-stroke;
  stroke-linejoin: round;
}
`;

function points(ring: number[][]): string {
  return ring.map((p) => `${p[0]},${p[1]}`).join(" ");
}

function rings(room: FloorplanRoom): number[][][] {
  return (room.polygon ?? []).filter((ring) => ring && ring.length >= 3);
}

/** Rooms that carry a reading and have something to draw on. */
export function occupancyRooms(rooms: FloorplanRoom[] | undefined): FloorplanRoom[] {
  return (rooms ?? []).filter((r) => r.inUse != null && rings(r).length > 0);
}

/** The busy ones only. */
export function busyRooms(rooms: FloorplanRoom[] | undefined): FloorplanRoom[] {
  return occupancyRooms(rooms).filter((r) => r.inUse === true);
}

/** Whether any room carries an occupancy reading, so the layer has something to toggle. */
export function hasOccupancyData(rooms: FloorplanRoom[] | undefined): boolean {
  return (rooms ?? []).some((r) => r.inUse != null);
}

/** Whether any placed item (a phone booth) carries an occupancy reading. */
export function hasItemOccupancy(rooms: FloorplanRoom[] | undefined, floorFurniture: FurnitureItem[] | undefined): boolean {
  const inRooms = (rooms ?? []).some((r) => (r.furniture ?? []).some((f) => f.occupied != null));
  return inRooms || (floorFurniture ?? []).some((f) => f.occupied != null);
}

const LEGEND_STYLE = `
.nodus-fp-occ-legend {
  position: absolute;
  right: 12px;
  bottom: 12px;
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 12px;
  background: var(--mantine-color-body, #fff);
  border: 1px solid var(--mantine-color-default-border, rgba(148, 163, 184, 0.4));
  pointer-events: none;
}
.nodus-fp-occ-legend span.swatch { display: inline-block; width: 18px; height: 12px; border-radius: 2px; margin-right: 6px; vertical-align: -2px; }
`;

/** Outside the zoom: what the stripes mean. Shown only while the Occupancy layer is on. */
export function FloorplanOccupancyLegend() {
  const stripes = (color: string) => `repeating-linear-gradient(45deg, ${color} 0 4px, transparent 4px 8px)`;
  return (
    <div className="nodus-fp-occ-legend" data-testid="floorplan-occupancy-legend" role="status">
      <style>{LEGEND_STYLE}</style>
      <div><span className="swatch" style={{ background: stripes(occupancyColor(true)) }} />In use</div>
      <div><span className="swatch" style={{ background: occupancyColor(false) }} />Free</div>
    </div>
  );
}

export function FloorplanBusyRooms({ rooms, mgmt = false }: { rooms: FloorplanRoom[]; mgmt?: boolean }) {
  const drawn = occupancyRooms(rooms);
  if (drawn.length === 0) return null;
  return (
    <g className="nodus-fp-busy-rooms" pointerEvents="none" data-testid="floorplan-busy-rooms">
      <style>{STYLE}</style>
      {drawn.map((r) => {
        const occupied = r.inUse === true;
        const base = getRoomColor(r, mgmt);
        const withOwnColor = hasOwnColor(base, occupied);
        const patternId = stripedPatternId(`room-${r.id}`);
        // Free rooms are solid (`freeColor`) and need no stroke: their fill already is a colour.
        const fill = occupied ? occupiedFill(patternId) : freeColor(base);
        const stroke = occupied ? occupancyColor(true) : undefined;
        return (
          <g key={r.id} className="nodus-fp-occ-room" data-room={r.id} data-in-use={occupied ? "true" : "false"} data-own-color={withOwnColor ? "true" : "false"} data-testid="floorplan-occupancy-room">
            {occupied && <defs><OccupancyStripes id={patternId} base={base} /></defs>}
            {rings(r).map((ring, i) => (
              <polygon key={i} points={points(ring)} fill={fill} fillOpacity={0.6} stroke={stroke} />
            ))}
          </g>
        );
      })}
    </g>
  );
}
